"use client";

import Link from "next/link";
import { useTranslation } from "react-i18next";
import {JSX, useEffect, useRef, useState} from "react";
import { usePathname } from "next/navigation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown, faAngleUp } from "@fortawesome/pro-regular-svg-icons";

import styles from "@@/styles/components/header/Header.module.scss";

import useMediaQuery from "@@/hooks/useMediaQuery";
import { HeaderCategoryContext } from "@@/hooks/contexts/headerCategroyContext";

import { montserratMedium } from "@@/utils/fonts";

import Dropdown from "./Dropdown";
import Hamburger from "../ui/Hamburger";
import LanguageSwitch from "./LanguageSwitch";
import MobileNavigation from "./mobile/MobileNavigation";

import MediawindLogo from "../ui/MediawindLogo";

import useHoverCapability from "@@/hooks/useHoverCapability";
import Button from "@@/components/ui/Button";

export enum HEADER_CATEGORY {
	SOLUTIONS = "Solutions",
	SECTORS = "sectors",
}

export default function Header(): JSX.Element {
	const [scrollTop, setScrollTop] = useState<boolean>(true);
	const [backgroundIsWhite, setBackgroundIsWhite] = useState<boolean>(false);
	const [backgroundIsTransparent, setBackgroundIsTransparent] = useState<boolean>(false);

	const [showNav, setShowNav] = useState<boolean>(false);
	const [hoveredCategory, setHoveredCategory] = useState<HEADER_CATEGORY | undefined>();

	const supportsHover = useHoverCapability();

	const toggleCategory = (cat: HEADER_CATEGORY) => {
		setHoveredCategory((prev) => (prev === cat ? undefined : cat));
	};
	const onCategoryPointerDown = (e: PointerEvent, cat: HEADER_CATEGORY) => {
		e.preventDefault();
		e.stopPropagation();
		toggleCategory(cat);
	};
	const onCategoryClick = (
		e: React.MouseEvent<HTMLAnchorElement>,
		category: HEADER_CATEGORY
	) => {
		// Empêche la navigation : on veut juste ouvrir le dropdown
		e.preventDefault();
		e.stopPropagation();

		setHoveredCategory(prev =>
			prev === category ? undefined : category
		);
	};

	const onCategoryKeyDown = (
		e: React.KeyboardEvent<HTMLAnchorElement>,
		category: HEADER_CATEGORY
	) => {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			setHoveredCategory(prev =>
				prev === category ? undefined : category
			);
		}

		if (e.key === "Escape") {
			setHoveredCategory(undefined);
		}
	};

	const isMobile = useMediaQuery("(max-width: 1200px)");
	const currentPathname = usePathname();
	const { t, } = useTranslation();

	const ref = useRef<HTMLDivElement>(null);

	const locales = ["fr", "en", "nl"] as const;

	function firstSegment(pathname: string) {
		const parts = pathname.split("/").filter(Boolean);
		const hasLocale = locales.includes(parts[0] as any);
		return (hasLocale ? parts[1] : parts[0]) ?? "";
	}

	const seg0 = firstSegment(currentPathname);
	const isHome = seg0 === "" ;

	useEffect(() => {
		const pageArray = currentPathname.split("/");
		const page = pageArray.slice(-1)[0];
		setBackgroundIsWhite((
			page === "easyqueue"
		));
		setBackgroundIsTransparent((
			page === "mediawind" ||
			page === "graphic-confection" ||
			page === "stylus-22h" ||
			page === "stylus-22v" ||
			page === "contact" ||
			page === "form-submitted" ||
			page === "about" ||
			page === "greenplayer" ||
			page === "kiosks" ||
			page === "totems" ||
			page === "screens" ||
			page === "services" ||
			page === "developpement_&_consultance"));
	}, [currentPathname]);

	useEffect(() => {
		const thresholdArray = [];
		let i = 0;

		while (i <= 1) {
			thresholdArray.push(i++);
		}

		const observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting) {
					setScrollTop(false);
				} else {
					setScrollTop(true);
				}
			});
		}, {
			threshold: thresholdArray,
		});

		if (ref.current && ref.current) {
			observer.observe(ref.current);
		}
	}, [ref, isMobile, currentPathname]);

	function closeHandler() {
		setShowNav(false);
	}

	if (isMobile) {
		return (
			<>
				<header className={styles.main}>
					<div className={styles.background_light} style={{ opacity: backgroundIsWhite || backgroundIsTransparent ? 1 : (scrollTop ? 0 : 1), }}></div>
					<MediawindLogo
						onClick={closeHandler}
						scrollTop={scrollTop}
						backgroundIsWhite={backgroundIsWhite}
						isMobile={isMobile}
					/>
					<Hamburger
						toggled={showNav}
						toggle={setShowNav}
						scrollTop={scrollTop}
						backgroundIsWhite={backgroundIsWhite}
					/>
				</header>

				<HeaderCategoryContext.Provider value={{ hoveredCategory, setHoveredCategory, }}>
					<MobileNavigation open={showNav} onClose={closeHandler} />
				</HeaderCategoryContext.Provider>
			</>
		);
	}

	function hoverHandler(category?: HEADER_CATEGORY) {
		setHoveredCategory(category);
	}

	return (
		<HeaderCategoryContext.Provider value={{ hoveredCategory, setHoveredCategory, }}>
			<div ref={ref} className={styles.scroll_detector}></div>

			<header
				className={`${styles.main}`}
				style={{ height: scrollTop ? "" : "5rem", }}
			>
				<div className={styles.background_light} style={{ opacity: backgroundIsWhite ? 1 : (scrollTop ? 0 : 1), }}></div>

				<MediawindLogo
					scrollTop={scrollTop}
					backgroundIsWhite={(backgroundIsWhite || backgroundIsTransparent)}
					isMobile={isMobile}
				/>

				<nav className={`${styles.navigation} ${(!backgroundIsWhite && !backgroundIsTransparent && scrollTop) ? styles.light : styles.dark}`}>
					<Link href="/"
						  className={`${styles.link} ${isHome ? styles.active : ""}`}
						  onMouseEnter={() => hoverHandler(undefined)}>
						<p className={montserratMedium.className}>
							{t("home")}
						</p>
					</Link>
					<Link href="/about"
						  className={`${styles.link} ${currentPathname.includes("about") ? styles.active : ""}`}
						  onMouseEnter={() => hoverHandler(undefined)}>
						<p className={montserratMedium.className}>
							{t("aboutus")}
						</p>
					</Link>
					<Link href="/developpement_&_consultance"
						  className={`${styles.link} ${currentPathname.includes("developpement_&_consultance") ? styles.active : ""}`}
						  onMouseEnter={() => hoverHandler(undefined)}>
						<p className={montserratMedium.className}>
							{t("dev&cons")}
						</p>
					</Link>
					<Link href="/services"
						  className={`${styles.link} ${currentPathname.includes("services") ? styles.active : ""}`}
						  onMouseEnter={() => hoverHandler(undefined)}>
						<p className={montserratMedium.className}>
							{t("services")}
						</p>
					</Link>
					<Link href="/solutions/greenplayer"
						  className={`${styles.link} ${currentPathname.includes("solutions") ? styles.active : ""}`}
						  onMouseEnter={supportsHover ? () => setHoveredCategory(HEADER_CATEGORY.SOLUTIONS) : undefined}
						  onClick={(e) => onCategoryClick(e, HEADER_CATEGORY.SOLUTIONS)}
						  onKeyDown={(e) => onCategoryKeyDown(e, HEADER_CATEGORY.SOLUTIONS)}
						  role="button"
						  tabIndex={0}
						  aria-haspopup="true"
						  aria-expanded={hoveredCategory === HEADER_CATEGORY.SOLUTIONS}
						  aria-controls="header-dropdown">
						<p className={montserratMedium.className}>
							{t("solutions")}
							<FontAwesomeIcon
								icon={hoveredCategory === HEADER_CATEGORY.SOLUTIONS ? faAngleUp : faAngleDown}
								className={styles.icon}
							/>
						</p>
					</Link>
					<Link href="/sectors/hospital_medical"
						  className={`${styles.link} ${currentPathname.includes('sectors') ? styles.active : ""}`}
						  onMouseEnter={supportsHover ? () => hoverHandler(HEADER_CATEGORY.SECTORS) : undefined}
						  onClick={(e) => onCategoryClick(e, HEADER_CATEGORY.SECTORS)}
						  onKeyDown={(e) => onCategoryKeyDown(e, HEADER_CATEGORY.SECTORS)}
						  role="button"
						  tabIndex={0}
						  aria-haspopup="true"
						  aria-expanded={hoveredCategory === HEADER_CATEGORY.SECTORS}
						  aria-controls="header-dropdown"
					>
						<p className={montserratMedium.className}>
							{t("sectors")}
							<FontAwesomeIcon
								icon={hoveredCategory === HEADER_CATEGORY.SECTORS ? faAngleUp : faAngleDown}
								className={styles.icon}
							/>
						</p>
					</Link>

					{/*
					<Link href="https://doc.greenplayer.com/qms" target="_blank" className={styles.link} onMouseEnter={() => hoverHandler(undefined)}>
						<p className={montserratMedium.className}>
							{t("documentation")}
						</p>
					</Link>
					*/}
					{/* <Link href="/" target="_blank" className={styles.link} onMouseEnter={() => hoverHandler(undefined)}>
						<p className={montserratMedium.className}>
							{t("realisations")}
						</p>
					</Link> */}
					{/* <Link href="/" target="_blank" className={styles.link} onMouseEnter={() => hoverHandler(undefined)}>
						<p className={montserratMedium.className}>
							{t("integration")}
						</p>
					</Link> */}
				</nav>

				<Button href={`/contact`} title={t("contact")} textColor={`var(--blue-800)`} bgColor={`var(--blue-300)`} className={`p-1 px-2 fs-0_8`} textClassName={`no-break`}></Button>

				<LanguageSwitch
					scrollTop={scrollTop}
					backgroundIsWhite={(backgroundIsWhite || backgroundIsTransparent)}
				/>
			</header>

			{hoveredCategory && <Dropdown scrollTop={scrollTop} />}
		</HeaderCategoryContext.Provider>
	);
}
